import * as React from "react";
import Select from "react-select";
import classnames from "classnames";
import MoneyInput from "./MoneyInput";
import LearnMore from "./shared/LearnMore";
import ValidateInput from "./forms/components/ValidateInput";
import ValidateSelect from "./forms/components/ValidateSelect";
import ssn from "./forms/normalizers/ssn";
import phone from "./forms/normalizers/phone";

const { Field } = require("redux-form");
const { FormGroup, ControlLabel } = require("react-bootstrap");

interface LabeledInputProps {
  inputType?: string;
  inputName?: string;
  inputID?: string;
  inputValue?: any;
  labelValue?: string;
  onChange?: Function;
  onChangeWithValue?: Function; // An interface if the client only needs the value
  onFocus?: React.FocusEventHandler<{}>;
  onBlur?: React.FocusEventHandler<{}>;
  autoFocus?: boolean;
  placeholder?: string;
  hint?: any;
  options?: any;
  valueKey?: string;
  className?: string;
  tooltip?: string;
  condensed?: boolean;
  onKeyDown?: Function;
  clearable?: boolean;
  dateFormat?: string;
  decimals?: boolean;
  min?: any;
  max?: any;
  radioLabel?: any;
  allowNegatives?: Boolean;
  checkboxLabel?: string;
  required?: Boolean;
  getRef?: Function;
  tabIndex?: number;
  formGroupClassName?: string;
  disabled?: boolean;
  autoComplete?: string;
}

export default class LabeledInput extends React.Component<LabeledInputProps, any> {
  public static defaultProps = {
    inputType: "text",
    inputValue: "",
    className: "form-control",
    clearable: true,
    disabled: false,
    autoComplete: "off",
  };

  handleOnChange(event) {
    if (this.props.onChange != null && this.props.onChangeWithValue == null) {
      this.props.onChange(event);
    } else if (this.props.onChange == null && this.props.onChangeWithValue != null) {
      this.props.onChangeWithValue((event.target && event.target.value) || event.value || null);
    }
  }

  renderInput() {
    switch (this.props.inputType) {
      case "boolean":
        return (
          <div className="form-control--checkbox">
            <input
              type="checkbox"
              id={this.props.inputID || this.props.inputName}
              name={this.props.inputName}
              checked={!!this.props.inputValue}
              onChange={this.handleOnChange.bind(this)}
              onFocus={this.props.onFocus}
              onBlur={this.props.onBlur}
              disabled={this.props.disabled}
            />
            <label htmlFor={this.props.inputID || this.props.inputName}>
              {this.props.checkboxLabel}
            </label>
          </div>
        );

      case "select":
        return (
          <Select
            className={this.props.className}
            clearable={this.props.clearable}
            placeholder={this.props.placeholder}
            value={this.props.inputValue}
            options={this.props.options}
            onChange={this.handleOnChange.bind(this)}
            onBlur={this.props.onBlur}
            disabled={this.props.disabled}
            autoComplete={this.props.autoComplete}
          />
        );

      case "money":
      case "validateMoney":
        return (
          <MoneyInput
            className={this.props.className}
            clearable={this.props.clearable}
            withRef
            tabIndex={this.props.tabIndex}
            getRef={this.props.getRef}
            type={this.props.inputType}
            id={this.props.inputID || this.props.inputName}
            name={this.props.inputName}
            value={this.props.inputValue}
            onChange={this.handleOnChange.bind(this)}
            onFocus={this.props.onFocus}
            onBlur={this.props.onBlur}
            onKeyDown={this.props.onKeyDown}
            min={this.props.min}
            max={this.props.max}
            placeholder={this.props.placeholder}
            decimals={this.props.decimals}
            allowNegatives={this.props.allowNegatives}
            required={this.props.required}
            disabled={this.props.disabled}
            autoComplete={this.props.autoComplete}
          />
        );

      case "validateSelect":
        const selectRequired = value => (value ? undefined : "This field is required");

        let validate = [];
        if (this.props.required) {
          validate = [selectRequired];
        }

        return (
          <Field
            className={this.props.className}
            withRef
            getRef={this.props.getRef}
            id={this.props.inputID}
            tabIndex={this.props.tabIndex}
            name={this.props.inputName}
            defaultValue={this.props.inputValue}
            onChange={this.handleOnChange.bind(this)}
            options={this.props.options}
            placeholder={this.props.placeholder}
            onKeyDown={this.props.onKeyDown}
            _onFocus={this.props.onFocus}
            inputValue={this.props.inputValue}
            valueKey={this.props.valueKey}
            component={ValidateSelect}
            validate={validate}
            disabled={this.props.disabled}
            autoComplete={this.props.autoComplete}
          />
        );

      case "validateText":
      case "validateNumber":
      case "validateEmail":
      case "validatePassword":
        return (
          <Field
            withRef
            getRef={this.props.getRef}
            className={this.props.className}
            type={this.props.inputType}
            tabIndex={this.props.tabIndex}
            name={this.props.inputName}
            component={ValidateInput}
            inputValue={this.props.inputValue}
            _onFocus={this.props.onFocus}
            placeholder={this.props.placeholder}
            label={this.props.placeholder}
            onUpdate={this.handleOnChange.bind(this)}
            onKeyDown={this.props.onKeyDown}
            disabled={this.props.disabled}
            autoComplete={this.props.autoComplete}
          />
        );
      case "validateRadio":
        return (
          <label>
            <Field
              className={this.props.className}
              type={this.props.inputType}
              name={this.props.inputName}
              component={ValidateInput}
              inputValue={this.props.inputValue}
              tabIndex={this.props.tabIndex}
              placeholder={this.props.placeholder}
              label={this.props.placeholder}
              onUpdate={this.handleOnChange.bind(this)}
              disabled={this.props.disabled}
            />
            {this.props.radioLabel}
          </label>
        );

      case "validateSSN":
        return (
          <Field
            name={this.props.inputName}
            component={ValidateInput}
            type={this.props.inputType}
            placeholder="###-###-###"
            normalize={ssn}
            maxlength="11"
            onUpdate={this.handleOnChange.bind(this)}
            onKeyDown={this.props.onKeyDown}
            disabled={this.props.disabled}
            autoComplete={this.props.autoComplete}
          />
        );

      case "validateTel":
        return (
          <Field
            name={this.props.inputName}
            withRef
            getRef={this.props.getRef}
            component={ValidateInput}
            type={this.props.inputType}
            normalize={phone}
            maxlength="12"
            onUpdate={this.handleOnChange.bind(this)}
            onKeyDown={this.props.onKeyDown}
            _onFocus={this.props.onFocus}
            placeholder={this.props.placeholder}
            disabled={this.props.disabled}
            autoComplete={this.props.autoComplete}
          />
        );

      case "validateTextarea":
        return (
          <Field
            className={this.props.className}
            name={this.props.inputName}
            placeholder={this.props.placeholder}
            component="textarea"
            disabled={this.props.disabled}
            autoComplete={this.props.autoComplete}
          />
        );

      case "textarea":
        return (
          <textarea
            className={this.props.className}
            autoFocus={this.props.autoFocus}
            id={this.props.inputID || this.props.inputName}
            name={this.props.inputName}
            placeholder={this.props.placeholder}
            value={this.props.inputValue}
            onBlur={this.props.onBlur}
            onChange={this.props.onChange.bind(this)}
            disabled={this.props.disabled}
            autoComplete={this.props.autoComplete}
          />
        );

      default:
        return (
          <input
            className="form-control"
            type={this.props.inputType}
            id={this.props.inputID || this.props.inputName}
            name={this.props.inputName}
            value={this.props.inputValue}
            onChange={this.handleOnChange.bind(this)}
            onFocus={this.props.onFocus}
            onBlur={this.props.onBlur}
            placeholder={this.props.placeholder}
            autoFocus={this.props.autoFocus}
            disabled={this.props.disabled}
            autoComplete={this.props.autoComplete}
          />
        );
    }
  }

  render() {
    const className = classnames({
      condensed: this.props.condensed,
      [this.props.formGroupClassName]: this.props.formGroupClassName,
    });

    return (
      <FormGroup className={className} controlId={this.props.inputName}>
        {this.props.labelValue && (
          <ControlLabel>
            {this.props.labelValue}
            {this.props.tooltip && <LearnMore text={this.props.tooltip} />}
          </ControlLabel>
        )}
        {this.props.hint !== undefined && (
          <span className="help-block" dangerouslySetInnerHTML={{ __html: this.props.hint }} />
        )}
        {this.renderInput()}
      </FormGroup>
    );
  }
}
